
import React from "react";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Schemes, Utils } from "../../../../utils";
import { Elements } from "../../../shared/Elements";
import AnalCard from "../../../shared/analytics/AnalCard";
import { ChartManager } from "../../../shared/ChartManager";
import { ChartData, ChartType, Column, DateHelper, DateTimeInput, DateTimePickerLayout, DateTimePickerSelectionMode, Dropdown, FormControl, StringHelper, Table, TextInput } from "@rivtn/noseur";
import { ApiResponseData, BaseService, EarningService, IMember, TransactionFlow, HostService, IHost, IHostTransaction, IHostAnalytics, SanitizedResponse, IHostManager, IHostManagerEarning, HostRole, IHostTournamentAnalytics } from "../../../../services";

export interface HostGeneralAnalyticsProps {
    profile: IMember;
}

function HostGeneralAnalytics(props: HostGeneralAnalyticsProps) {

    const { profile } = props;
    const hostService = HostService.getInstance();
    const labels = i18nManager.Labels.host.analytics;
    let { id: hostId = "" } = ReactRouterDom.useParams();
    const [host, setHost] = React.useState<IHost>({} as any);
    const hostAnalyticsChartDataState = React.useState<ChartData>();
    const [daysDifference, setDaysDifference] = React.useState<number>(0);
    //const [topPrograms, setTopPrograms] = React.useState<IHostTopProgram[]>();
    const [hostAnalytics, setHostAnalytics] = React.useState<IHostAnalytics>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [hostTransactions, setHostTransactions] = React.useState<ApiResponseData<IHostTransaction>>({});
    const [hostTournamentAnalytics, setHostTournamentAnalytics] = React.useState<IHostTournamentAnalytics[]>();
    const currencySign = i18nManager.getCurrencySign(searchParams.get("curr") ?? "NGN");
    const [hostTournamentAnalyticType, setHostTournamentAnalyticType] = React.useState<"EARNINGS" | "PARTICIPANTS">(searchParams.get("htanal") as any ?? "EARNINGS");

    React.useEffect(fetchInformation, []);

    React.useEffect(() => {
        setDaysDifference(DateHelper.getDaysBetweenDates(
            (searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()),
            (searchParams.has("to") ? new Date(searchParams.get("to")!) : new Date())));
    }, [searchParams]);

    return (<div className="earnings analytics">
        <div className="header">
            <div className="l" style={{ display: "flex", flexDirection: "column" }}>
                <span className="t lined-limit"><i className="fa fa-arrow-left" onClick={() => window.history.back()} />
                    {i18nManager.Labels.host.title + " - " + hostId + " - " + labels.title}
                </span>
                <span className="lined-limit" style={{ marginLeft: 25, fontWeight: "normal" }}>{host.name}</span>
            </div>
            <div className="queries">
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: i18nManager.Labels.teams.analytics.country_currency, }}
                    selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === (searchParams.get("curr") ?? "NGN")) ?? 0)}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={i18nManager.CountriesAndCurrencies} optionMap={{
                        value: "currency_code",
                        label: "{label} ({currency_name} - {currency_code})",
                    }} onSelectOption={(option) => {
                        Utils.updateSearchParams("curr", option.currency_code, setSearchParams, fetchInformation);
                    }} />
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={i18nManager.Labels.teams.analytics.date_range} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchInformation);
                        }} />
                </FormControl>
            </div>
        </div>
        <div className="container">
            <div className="primary">
                <FormControl isFieldset fill label={labels.tournaments}>
                    <TextInput />
                </FormControl>
                <div className="anal-cards" style={{ marginTop: 15 }}>
                    <AnalCard anal={hostAnalytics?.earnings?.total} currencySign={currencySign} desc={labels.earned_within_x_days} title={labels.total_earnings}
                        icon="fas fa-sack-dollar" daysDifference={daysDifference} to={{ pathname: `./earnings`, search: window.location.search }} />
                    <AnalCard anal={hostAnalytics?.tournaments} desc={labels.tournament_hosted} title={labels.tournaments}
                        icon="fas fa-basketball-ball" analKey="count" daysDifference={daysDifference} to={{ pathname: `./../settings`, search: "tab=MANAGE_TOURNAMENTS" }} cardColor={"#EBFEF6"} />
                    <AnalCard anal={hostAnalytics?.programs} desc={labels.programs.toLowerCase()} title={labels.programs}
                        icon="fa fa-trophy" analKey="count" daysDifference={daysDifference} cardColor={"#EBF4FE"} />
                </div>
                {<ChartManager type={ChartType.BAR} title={labels.host_anals} chartDataState={hostAnalyticsChartDataState} />}
                <div className="anal-table">
                    <div className="header">
                        <span className="t">{labels.transactions.last_10_earnings}</span>
                        <ReactRouterDom.NavLink to={{ pathname: `/hosts/${hostId}/analytics/transactions`, search: window.location.search }}>{labels.transactions.view_all_trans}</ReactRouterDom.NavLink>
                    </div>
                    <Table stripedRows scheme={Schemes.OMNEHOST} data={hostTransactions.content} totalRecords={hostTransactions.total_elements} dataRefreshKeys={["external_id", "created_at"]}
                        style={{ flex: 1 }} rowsPerPage={200} loadingState={Elements.LOADING_STATE} emptyState={Elements.buildEmptyListView(undefined, (<span>
                            {labels.transactions.empty_label_parts.no_earns_found}{" "}
                            <ReactRouterDom.NavLink to="/hosts/create-tournament">{labels.transactions.empty_label_parts.create}</ReactRouterDom.NavLink>{" "}
                            {labels.transactions.empty_label_parts.a_tours}{" "}
                            <ReactRouterDom.NavLink to={`/hosts/${hostId}/merch`}>{labels.transactions.empty_label_parts.create_merch}</ReactRouterDom.NavLink>{" "}
                            {labels.transactions.empty_label_parts.to_gen_income}
                        </span>), { marginTop: 50 })}>
                        <Column className="table-c" header={labels.transactions.table.headers.date} dataKey="date" template={(v) => Utils.formatToFineDate(v, true)} />
                        <Column className="table-c" header={labels.transactions.table.headers.amount} dataKey="amount" template={(v: number) => (`${currencySign} ${StringHelper.toPresentableMoneyValue(v)}`)} />
                        <Column className="table-c" header={labels.transactions.table.headers.source} template={(v: IHostTransaction) => {
                            return "TODO";
                            /*let link = EarningService.buildTransactionSourceLink(v);
                            const label = v.source?.label ?? v.source?.extra_data?.name;
                            return (<div style={{ display: "flex", flexDirection: "row", alignItems: "center", }}>
                                {v.source?.extra_data?.logo && (<img alt={label} src={v.source?.extra_data?.logo} style={{ width: 20, height: 20, marginRight: 10, borderRadius: "100%" }} />)}
                                {link ? (<a target="_blank" href={link}>{label}</a>) : (<span>{label}</span>)}
                            </div>);*/
                        }} />
                        <Column className="table-c" header={labels.transactions.table.headers.recipient} template={(v: IHostTransaction) => ((v.recipient as IHostManager).member?.username)} />
                    </Table>
                </div>
            </div>
            <div className="secondary">
                <div className="tt-earners" style={{ backgroundColor: (hostTournamentAnalyticType === "EARNINGS" ? "#EDEBFE" : "#FEFBEB") }}>
                    <span className="t">{hostTournamentAnalyticType === "EARNINGS" ? labels.tournaments_by_earnings : labels.tournaments_by_participant}</span>
                    {!hostTournamentAnalytics && Elements.LOADING_STATE}
                    <div className="listers">
                        {hostTournamentAnalytics && !hostTournamentAnalytics?.length && (Elements.buildEmptyListView(undefined, (<span>
                            {labels.top_earning.empty_label_parts.no_tours_found}{" "}
                            <ReactRouterDom.NavLink to="/hosts/create-tournament">{labels.top_earning.empty_label_parts.create}</ReactRouterDom.NavLink>{" "}
                            {labels.top_earning.empty_label_parts.a_tour_to_be}
                        </span>)))}
                        {hostTournamentAnalytics?.map((hostTournamentAnalytic) => {
                            const isEarningAnal = hostTournamentAnalyticType === "EARNINGS";
                            const tournamentEdition = hostTournamentAnalytic.tournament_edition;

                            return (<ReactRouterDom.NavLink key={tournamentEdition.external_id} className="tournament"
                                to={`/hosts/${hostId}/tournament/${tournamentEdition.tournament.public_id}`}>
                                <div className="banner">
                                    <img className="logo" alt={tournamentEdition.name} src={tournamentEdition?.logo_url} />
                                    <img className="ba" alt={tournamentEdition.name} src={tournamentEdition.banner_url} />
                                </div>
                                <div className="details">
                                    <span className="name lined-limit">{tournamentEdition.name}</span>
                                    <span className="value lined-limit">
                                        {isEarningAnal && (currencySign + " ")}
                                        {isEarningAnal ? StringHelper.toPresentableMoneyValue(hostTournamentAnalytic.value) : hostTournamentAnalytic.value}
                                        {!isEarningAnal && (" " + labels.top_earning.unique_participants)}
                                    </span>
                                    <div className="info">
                                        <div>
                                            <span className="lined-limit">{labels.top_earning.program}</span>
                                            <span className="lined-limit">Need For Speed: Payback</span>
                                        </div>
                                        <div>
                                            <span className="lined-limit">{labels.top_earning.location}</span>
                                            <span className="lined-limit">2 Road Ares, Ikeja, Lagos</span>
                                        </div>
                                        <div>
                                            <span className="lined-limit">{labels.top_earning.recurring}</span>
                                            <span className="lined-limit">Yes</span>
                                        </div>
                                    </div>
                                </div>
                            </ReactRouterDom.NavLink>);
                        })}
                    </div>
                </div>
                <div className="tt-earners host-sec" style={{ backgroundColor: (hostTournamentAnalyticType === "PARTICIPANTS" ? "#EDEBFE" : "#FEFBEB") }} onClick={() => {
                    const newTourAnalType = hostTournamentAnalyticType === "EARNINGS" ? "PARTICIPANTS" : "EARNINGS";
                    Utils.updateSearchParams("htanal", newTourAnalType, setSearchParams, () => {
                        setHostTournamentAnalyticType(newTourAnalType);
                        queryHostTournamentAnalytics();
                    });
                }}>
                    <span className="t">{hostTournamentAnalyticType === "EARNINGS" ? labels.tournaments_by_participant : labels.tournaments_by_earnings}</span>
                </div>
            </div>
        </div>
    </div>);

    function fetchInformation() {
        hostService.getHostByPublicId(hostId!).then(({ sanitized }: SanitizedResponse<IHost>) => {
            setHost(sanitized);
        }).catch(BaseService.reportError);

        const params = {
            currency_code: searchParams.get("curr") ?? "NGN",
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            fields: ["earnings.total", "tournaments.entered", "achievements.total"],
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        };
        setHostAnalytics(undefined);
        hostService.getHostAnalytics(hostId, params).then(({ sanitized }: SanitizedResponse<IHostAnalytics>) => {
            setHostAnalytics(sanitized);
        }).catch(BaseService.reportError);

        hostAnalyticsChartDataState[1](undefined);
        hostService.queryAnalyticsChartData(hostId, params).then(({ apiResponse }: SanitizedResponse<any>) => {
            hostAnalyticsChartDataState[1](apiResponse.data!);
        }).catch(BaseService.reportError);

        setHostTransactions({});
        hostService.queryTransactions(hostId, { ...params, size: 10, flow: TransactionFlow.OUT }).then(({ apiResponse }: SanitizedResponse<any>) => {
            setHostTransactions(apiResponse.data!);
        }).catch(BaseService.reportError);

        queryHostTournamentAnalytics(params);
    }

    function queryHostTournamentAnalytics(params?: any) {
        setHostTournamentAnalytics(undefined);
        hostService.getHostTournamentAnalytics(hostId, params ?? {
            currency_code: searchParams.get("curr") ?? "NGN",
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            fields: ["earnings.total", "tournaments.entered", "achievements.total"],
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        }).then(({ content }: SanitizedResponse<IHostTournamentAnalytics>) => {
            setHostTournamentAnalytics(content);
        }).catch(BaseService.reportError);
    }

}

export default HostGeneralAnalytics;
